import _typeof from "@babel/runtime/helpers/typeof";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Children, createContext, useContext, useMemo } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import noop from '@atlaskit/ds-lib/noop';
import { durations, exitingDurations, ExitingPersistence, SlideIn } from '@atlaskit/motion';
import Portal from '@atlaskit/portal';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { gridSize as getGridSize, layers } from '@atlaskit/theme/constants';
import VisuallyHidden from '@atlaskit/visually-hidden';
var gridSize = getGridSize();
export var flagWidth = gridSize * 50;
var defaultFlagGroupContext = {
  onDismissed: noop,
  isDismissAllowed: false
};

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export var FlagGroupContext = /*#__PURE__*/createContext(defaultFlagGroupContext);

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export function useFlagGroup() {
  return useContext(FlagGroupContext);
}

// transition: none is set on first-of-type to prevent a bug in Firefox
// that causes a broken transition
var baseStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  width: flagWidth,
  position: 'absolute',
  insetBlockEnd: 0,
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
  transition: "transform ".concat(durations.medium, "ms ease-in-out"),
  // TODO: Use new breakpoints
  // eslint-disable-next-line @atlaskit/design-system/no-nested-styles
  '@media (max-width: 560px)': {
    width: '100vw'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
  ':first-of-type': {
    transform: "translate(0,0)",
    transition: 'none'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
  ':nth-of-type(n + 2)': {
    animationDuration: '0ms',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
    transform: "translateX(0) translateY(100%) translateY(".concat(2 * gridSize, "px)")
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
  ':nth-of-type(1)': {
    zIndex: 5
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
  ':nth-of-type(2)': {
    zIndex: 4
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
  '&:nth-of-type(n + 4)': {
    visibility: 'hidden'
  }
});

// Transform needed to push up while 1st flag is leaving
// Exiting time should match the exiting time of motion so is halved
var dismissAllowedStyles = css({
  // eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
  '&& + *': {
    transform: "translate(0, 0)",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
    transitionDuration: "".concat(exitingDurations.medium, "ms")
  }
});
var flagGroupContainerStyles = css({
  position: 'fixed',
  zIndex: 'flag',
  insetBlockEnd: "var(--ds-space-600, 48px)",
  insetInlineStart: "var(--ds-space-1000, 80px)",
  // TODO: Use new breakpoints
  // eslint-disable-next-line @atlaskit/design-system/no-nested-styles
  '@media (max-width: 560px)': {
    insetBlockEnd: 0,
    insetInlineStart: 0
  }
});

/**
 * __Flag group__
 *
 * A flag group is used to group a set of related flags, with entry and exit animations.
 *
 * - [Examples](https://atlassian.design/components/flag/flag-group/examples)
 * - [Code](https://atlassian.design/components/flag/flag-group/code)
 */
var FlagGroup = function FlagGroup(props) {
  var id = props.id,
    _props$label = props.label,
    label = _props$label === void 0 ? 'Flag notifications' : _props$label,
    _props$labelTag = props.labelTag,
    LabelTag = _props$labelTag === void 0 ? 'h2' : _props$labelTag,
    _props$shouldRenderTo = props.shouldRenderToParent,
    shouldRenderToParent = _props$shouldRenderTo === void 0 ? false : _props$shouldRenderTo,
    children = props.children,
    _props$onDismissed = props.onDismissed,
    onDismissed = _props$onDismissed === void 0 ? noop : _props$onDismissed;
  var hasFlags = Array.isArray(children) ? children.length > 0 : Boolean(children);
  var dismissFlagContext = useMemo(function () {
    return {
      onDismissed: onDismissed,
      isDismissAllowed: true
    };
  }, [onDismissed]);
  var renderChildren = function renderChildren() {
    return children && _typeof(children) === 'object' ? Children.map(children, function (flag, index) {
      var isDismissAllowed = index === 0;
      return jsx(SlideIn, {
        enterFrom: "left",
        fade: "inout",
        duration: "medium",
        animationTimingFunction: "ease-in"
      }, function (_ref) {
        var className = _ref.className,
          ref = _ref.ref;
        return jsx("div", {
          css: [baseStyles, isDismissAllowed && dismissAllowedStyles]
          // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
          ,
          className: className,
          ref: ref
        }, jsx(FlagGroupContext.Provider, {
          value:
          // Only the first flag should be able to be dismissed.
          isDismissAllowed ? dismissFlagContext : defaultFlagGroupContext
        }, flag));
      });
    }) : false;
  };
  var flags = jsx("div", {
    id: id,
    css: flagGroupContainerStyles
  }, hasFlags ? jsx(VisuallyHidden, null, jsx(LabelTag, null, label)) : null, jsx(ExitingPersistence, {
    appear: false
  }, renderChildren()));
  return shouldRenderToParent ? flags : jsx(Portal, {
    zIndex: layers.flag()
  }, flags);
};
export default FlagGroup;